import { useMemo, useState } from 'react';
import Reveal from '@/components/base/Reveal';
import SectionHeading from '@/components/base/SectionHeading';
import { animeGenres, animeLibrary } from '@/mocks/animeData';

export default function LibrarySection() {
  const [genre, setGenre] = useState('全部');
  const [keyword, setKeyword] = useState('');

  const filtered = useMemo(() => {
    const kw = keyword.trim().toLowerCase();
    return animeLibrary.filter((item) => {
      const matchGenre = genre === '全部' || item.genre === genre;
      const matchKw =
        !kw ||
        item.title.toLowerCase().includes(kw) ||
        item.summary.toLowerCase().includes(kw) ||
        item.genre.toLowerCase().includes(kw);
      return matchGenre && matchKw;
    });
  }, [genre, keyword]);

  return (
    <section id="library" className="w-full py-16 md:py-24 bg-background-100">
      <div className="px-4 md:px-8">
        <Reveal>
          <SectionHeading
            eyebrow="Library"
            title="作品速查库"
            description="按类型筛选，或直接输入关键词，在本期收录的作品里快速定位。所有条目都留在本页，筛选只为帮你少翻几次。"
          />
        </Reveal>

        <Reveal>
          <div className="mt-8 flex flex-col gap-4">
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
              <div className="relative flex-1 max-w-md">
                <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
                <input
                  type="text"
                  value={keyword}
                  onChange={(e) => setKeyword(e.target.value)}
                  placeholder="输入作品名或类型关键词…"
                  className="w-full pl-10 pr-10 py-2.5 text-sm rounded-md bg-background-50 border border-background-300/70 text-foreground-900 placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                />
                {keyword && (
                  <button
                    type="button"
                    aria-label="清空搜索"
                    onClick={() => setKeyword('')}
                    className="absolute right-2 top-1/2 -translate-y-1/2 w-7 h-7 flex items-center justify-center rounded-md text-foreground-400 hover:text-foreground-700 hover:bg-background-200 cursor-pointer"
                  >
                    <i className="ri-close-line"></i>
                  </button>
                )}
              </div>
              <span className="text-xs md:text-sm text-foreground-500 whitespace-nowrap">
                共 {filtered.length} 部符合条件
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              {animeGenres.map((g) => (
                <button
                  key={g}
                  type="button"
                  onClick={() => setGenre(g)}
                  className={`px-3.5 py-1.5 text-xs md:text-sm rounded-full whitespace-nowrap transition-colors cursor-pointer ${
                    genre === g
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-50 text-foreground-700 border border-background-300/70 hover:border-primary-300 hover:text-primary-600'
                  }`}
                >
                  {g}
                </button>
              ))}
            </div>
          </div>
        </Reveal>

        {filtered.length > 0 ? (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4 md:gap-5">
            {filtered.map((item, index) => (
              <Reveal key={item.id} delay={Math.min(index * 40, 240)}>
                <article className="group h-full flex flex-col rounded-lg bg-background-50 border border-background-200/70 overflow-hidden hover:border-primary-300 transition-colors">
                  <div className="relative w-full h-56 overflow-hidden bg-background-200">
                    <img
                      src={item.poster}
                      alt={`${item.title} 动漫作品封面`}
                      className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                    />
                    <div className="absolute top-3 left-3 flex flex-col gap-1.5 items-start">
                      <span className="px-2 py-0.5 rounded-full text-[11px] bg-background-50/90 text-foreground-800">
                        {item.genre}
                      </span>
                    </div>
                    <span
                      className={`absolute top-3 right-3 px-2 py-0.5 rounded-full text-[11px] ${
                        item.status === '更新中'
                          ? 'bg-primary-500 text-background-50'
                          : item.status === '新番'
                            ? 'bg-accent-500 text-background-50'
                            : 'bg-foreground-800/85 text-background-50'
                      }`}
                    >
                      {item.status}
                    </span>
                    <div className="absolute bottom-2 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-black/55 text-background-50 text-xs">
                      <i className="ri-star-fill text-accent-400"></i>
                      {item.score}
                    </div>
                  </div>

                  <div className="flex-1 flex flex-col p-4">
                    <h3 className="font-heading text-base font-bold text-foreground-950">
                      {item.title}
                    </h3>
                    <div className="mt-1 flex items-center gap-3 text-xs text-foreground-500">
                      <span className="flex items-center gap-1">
                        <i className="ri-time-line"></i>
                        {item.episodes}
                      </span>
                      <span>{item.year} 年</span>
                    </div>
                    <p className="mt-3 text-xs md:text-sm text-foreground-600 leading-relaxed line-clamp-3">
                      {item.summary}
                    </p>
                    <p className="mt-3 pt-3 border-t border-background-200 text-xs text-foreground-500 leading-relaxed line-clamp-2">
                      <i className="ri-lightbulb-line text-accent-600 mr-1"></i>
                      {item.highlight}
                    </p>
                  </div>
                </article>
              </Reveal>
            ))}
          </div>
        ) : (
          <div className="mt-10 flex flex-col items-center justify-center py-16 rounded-lg bg-background-50 border border-dashed border-background-300 text-center">
            <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
            <p className="mt-3 text-sm text-foreground-600">没有找到符合条件的作品</p>
            <button
              type="button"
              onClick={() => {
                setGenre('全部');
                setKeyword('');
              }}
              className="mt-4 px-4 py-2 text-sm rounded-md bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors cursor-pointer"
            >
              重置筛选条件
            </button>
          </div>
        )}
      </div>
    </section>
  );
}